import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiLoginResponse,
  ApiSession,
  GetUserDetailsParams,
  GetUserDetailsResponse,
  KeycloakUser
} from "./types";

export type AuthState = {
  isApiLoggingIn: boolean;
  isApiLoggingOut: boolean;
  isApiReloggingIn: boolean;
  isLoggingOut: boolean;
  isUserLoading: boolean;
  user: KeycloakUser | null;
  apiSession: ApiSession | null;
};

const initialState: AuthState = {
  apiSession: null,
  isApiLoggingIn: false,
  isApiLoggingOut: false,
  isApiReloggingIn: false,
  isLoggingOut: false,
  isUserLoading: false,
  user: null
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Get User Details
    getUserDetailsRequest: (
      state,
      action: PayloadAction<GetUserDetailsParams>
    ) => {
      state.isUserLoading = true;
    },
    getUserDetailsSuccess: (
      state,
      action: PayloadAction<GetUserDetailsResponse>
    ) => {
      state.isUserLoading = false;
      state.user = action.payload;
    },
    getUserDetailsFailure: (
      state,
      action: PayloadAction<{ params: GetUserDetailsParams; error: string }>
    ) => {
      state.isUserLoading = false;
    },

    // API Login
    apiLoginRequest: (state) => {
      state.isApiLoggingIn = true;
    },
    apiLoginSuccess: (state, action: PayloadAction<ApiLoginResponse>) => {
      state.isApiLoggingIn = false;
      state.apiSession = action.payload;
    },
    apiLoginFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.isApiLoggingIn = false;
    },

    // API Logout
    apiLogoutRequest: (state) => {
      state.isApiLoggingOut = true;
    },
    apiLogoutSuccess: (state) => {
      state.isApiLoggingOut = false;
    },
    apiLogoutFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.isApiLoggingOut = false;
    },

    // API Relogin
    apiReloginRequest: (state) => {
      state.isApiReloggingIn = true;
    },
    apiReloginSuccess: (state) => {
      state.isApiReloggingIn = false;
    },
    apiReloginFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.isApiReloggingIn = false;
    },

    // Logout
    logoutRequest: (state) => {
      state.isLoggingOut = true;
    },
    logoutSuccess: (state) => {
      state.isLoggingOut = false;
    },
    logoutFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.isLoggingOut = false;
    },

    // Refresh API Session Polling
    startRefreshApiSessionPolling: () => {},
    stopRefreshApiSessionPolling: () => {}
  }
});

export const {
  apiLoginRequest,
  apiLoginSuccess,
  apiLoginFailure,
  apiLogoutRequest,
  apiLogoutSuccess,
  apiLogoutFailure,
  apiReloginRequest,
  apiReloginSuccess,
  apiReloginFailure,
  logoutRequest,
  logoutSuccess,
  logoutFailure,
  getUserDetailsRequest,
  getUserDetailsSuccess,
  getUserDetailsFailure,
  startRefreshApiSessionPolling,
  stopRefreshApiSessionPolling
} = authSlice.actions;

export const { actions: authActions } = authSlice;

export const reducer = authSlice.reducer;
